import { StationType } from '@mi-front/core';
import CellTowerIcon from '@mui/icons-material/CellTower';
import ClassIcon from '@mui/icons-material/Class';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { darken } from '@mui/material';
import { $T } from '../../../locale/types';
import {
  stationFilterOptionTypeLongMessages,
  stationFilterOptionTypeShortMessages,
  stationTypeMessages,
} from '../station-messages';
import { OptionType, StationFilterOption } from './types';

/** Maps OptionType to the key used in station filter option type messages. */
const optionTypeToMessageKey: Record<StationFilterOption['type'], string> = {
  [OptionType.StationType]: 'stationType',
  [OptionType.StationGroup]: 'stationGroup',
  [OptionType.IndividualStation]: 'individualStation',
};

/** Returns the localized label for a station type (e.g. Independiente, Cabecera). */
export const getStationTypeLabel = ($t: $T, type: StationType): string =>
  $t(stationTypeMessages[type]);

// Helper function to generate ID from type and value
export const generateOptionId = (option: StationFilterOption): string => {
  switch (option.type) {
    case OptionType.StationType:
      return `${option.type}-${option.value}`;
    case OptionType.StationGroup:
      return `${option.type}-${option.value.id}`;
    case OptionType.IndividualStation:
      return `${option.type}-${option.value.id}`;
    default:
      return '';
  }
};

// Helper function to get icon for option type
export const getOptionIcon = (type: StationFilterOption['type']) => {
  switch (type) {
    case OptionType.StationType:
      return <ClassIcon fontSize="inherit" />;
    case OptionType.StationGroup:
      return <WorkspacesIcon fontSize="inherit" />;
    case OptionType.IndividualStation:
      return <CellTowerIcon fontSize="inherit" />;
    default:
      return null;
  }
};

// Helper function to get color for option type
export const getOptionColor = (type: StationFilterOption['type']) => {
  switch (type) {
    case OptionType.StationType:
      return '#e3f2fd'; // Light blue
    case OptionType.StationGroup:
      return '#fff3e0'; // Light orange
    case OptionType.IndividualStation:
      return '#e8f5e8'; // Light green
    default:
      return '#f5f5f5'; // Default light gray
  }
};

/** Returns the localized group header for the given option type and variant. */
export const getGroupHeader = (
  $t: $T,
  type: StationFilterOption['type'],
  variant: 'short' | 'long',
): string => {
  const key = optionTypeToMessageKey[type];
  if (!key) return '';
  const messages =
    variant === 'long' ? stationFilterOptionTypeLongMessages : stationFilterOptionTypeShortMessages;
  return $t(messages[key]);
};

// Helper function to get chip styles
export const getChipStyles = (type: StationFilterOption['type']) => ({
  backgroundColor: getOptionColor(type),
  borderColor: darken(getOptionColor(type), 0.2),
});

// Helper function to sort options by type priority
export const sortOptionsByType = (options: StationFilterOption[]): StationFilterOption[] => {
  const typeOrder = [OptionType.StationType, OptionType.StationGroup, OptionType.IndividualStation];

  return [...options].sort((a, b) => {
    // First sort by type priority
    const aTypeIndex = typeOrder.indexOf(a.type);
    const bTypeIndex = typeOrder.indexOf(b.type);

    if (aTypeIndex !== bTypeIndex) {
      return aTypeIndex - bTypeIndex;
    }

    // Then sort alphabetically within each type
    return a.label.localeCompare(b.label);
  });
};
